/**
 *
 * @module controls
 * @author Shojib [Mahmud.Hossain@mlb.com]
 * @description Scores Collection Horizontal Controls
 *
 */

import util from 'util';
import cloneDeep from 'lodash-es/cloneDeep';
import assign from 'lodash-es/assign';
import create from 'lodash-es/create';

const ITEM_WIDTH_BY_MODE = {
    minisb: {
        sm: 105,
        lg: 105
    },
    'game-selector': {
        sm: 60,
        lg: 69
    }
};
const SCREEN_MD = 768;

/**
 *
 * @name scoresControls
 *
 */
class scoresControls {

    constructor(opts, scores) {

        /**
         *
         * @name opts
         * @description scores controls default options
         *
         */
        this.opts = opts;

        /**
         *
         * @name scores
         * @description the master scores
         *
         */
        this.scores = scores;

        /**
         *
         * @name mode
         * @description current mode
         *
         */
        this.mode = opts.mode;

        /**
         *
         * @name core
         * @description scores controls core properties
         *
         */
        this.core = {
            animationTimer: null,
            animationInProgress: false,
            resizeThrottleEvent: null,
            scrollThrottleEvent: null
        };

        /**
         *
         * @name props
         * @description scores controls properties
         *
         */
        this.props = {
            distance: 50,
            instance: false,
            isEnd: false,
            isBeginning: false,
            elementList: null,
            elementLeft: null,
            elementRight: null,
            controlListSelector: '.mlb-scores__list--games',
            controlLeftSelector: '.mlb-scores__list-button--left',
            controlRightSelector: '.mlb-scores__list-button--right',
            MIG: {
                swiper: null
            }
        };

        /**
         *
         * @name eventCallbacks
         * @description callbacks for scores events
         *
         */
        this.eventCallbacks = {
            windowResizeCallback: (e) => {

                const { core } = this;
                const { resizeThrottleEvent } = core;

                clearTimeout(resizeThrottleEvent);

                core.resizeThrottleEvent = setTimeout(() => {

                    this.showHideControls();
                    this.updatePosition();

                }, 100);
            },
            scrollCallback: (e) => {

                const { core } = this;
                const { scrollThrottleEvent } = core;

                clearTimeout(scrollThrottleEvent);

                core.scrollThrottleEvent = setTimeout(() => {

                    this.updatePosition();

                }, 100);
            },
            leftClickCallback: (e) => {

                const { props } = this;
                const { distance } = props;

                this.animateScroll('left', distance);

            },
            rightClickCallback: (e) => {

                const { props } = this;
                const { distance } = props;

                this.animateScroll('right', distance);

            }
        };

    }

    /**
     *
     * @method shouldShowControls
     * @description determines if controls should be visible based on container width and num games
     * @param {Object} opts
     * @param {Object} props
     * @returns {Boolean}
     *
     */
    shouldShowControls() {

        const { opts, props } = this;
        const { mode, container } = opts;

        if (mode === 'MIG') {

            return true;

        }

        const elementList = props.elementList;
        const gameElements = elementList.querySelectorAll('.mlb-scores__list-item--game');
        const containerWidth = container.clientWidth;
        const screenMd = window.innerWidth >= SCREEN_MD;
        const itemWidth = ITEM_WIDTH_BY_MODE[mode][screenMd ? 'lg' : 'sm'];

        return (gameElements.length * itemWidth) > containerWidth;

    }

    /**
     *
     * @method updateSwiper
     * @description updates swiper depending on how many mig widgets in a single page
     *
     */
    updateSwiper(swiper) {
        const { props, opts } = this;
        const { LazyLoadMIG } = opts;
        const length = LazyLoadMIG.allDates.length;

        props.isEnd = swiper.isEnd;
        props.isBeginning = swiper.isBeginning;

        // Lazy load the previous and next MIG games
        if (props.isBeginning || props.isEnd) {

            const lastIndex = length > 0 ? (length - 1) : length;
            const date = LazyLoadMIG.allDates[
                props.isBeginning ? 0 : lastIndex
            ];

            this.scores.emit('load-date', date);

        }
    }

    /**
     *
     * @method updatePosition
     * @description updates properties based on current position
     *
     */
    updatePosition() {

        const { props, opts } = this;
        const { elementList, elementLeft, elementRight } = props;
        const { offsetWidth, scrollLeft, scrollWidth } = elementList;
        const { mode } = opts;

        if (mode === 'MIG') {

            const { MIG } = props;
            const { swiper } = MIG;

            this.updateSwiper(swiper);

        } else {

            props.isEnd = scrollLeft + offsetWidth >= scrollWidth;
            props.isBeginning = scrollLeft === 0;

        }

        const { isEnd, isBeginning } = props;

        /* eslint-disable no-unused-expressions */
        isBeginning ? elementLeft.setAttribute('disabled', '') : elementLeft.removeAttribute('disabled');
        isEnd ? elementRight.setAttribute('disabled', '') : elementRight.removeAttribute('disabled');
        /* eslint-enable no-unused-expressions */

    }

    /**
     *
     * @method scrollToGame
     * @param {String} gamePk
     * @returns {Object} this
     *
     */
    scrollToGame(gamePk = '') {

        const { props } = this;
        const { elementList } = props;

        const gameElement = elementList.querySelector(`[data-game-pk="${gamePk}"]`);
        const gameOffset = gameElement.offsetParent.offsetLeft;
        const scrollAmount = gameOffset;

        util.log('mlbScores - scroll to game', gamePk);

        elementList.scrollLeft = scrollAmount;

        return this;

    }

    /**
     *
     * @method animateScroll
     * @description animate scroll to a direction
     * @param {String} direction
     * @param {Number} distance
     * @todo clean this up a bit
     *
     */
    animateScroll(direction = 'right', distance = 0) {

        const { core, props } = this;
        const { elementList } = props;

        const tick = 1;
        let tickDistance = 0;
        const tickPx = 5;
        const isLeft = direction === 'left';
        const isRight = direction === 'right';

        //
        // if animation is already in progress, ignore execution
        //
        if (core.animationInProgress) {
            return;
        }

        //
        // animate scroll to direction
        //
        core.animationTimer = setInterval(() => {

            core.animationInProgress = true;
            tickDistance += tick;

            if (isRight) {
                elementList.scrollLeft += tickPx;
            }

            if (isLeft) {
                elementList.scrollLeft -= tickPx;
            }

            if ((isRight && props.isEnd)
                || (isLeft && props.isBeginning)
                || (tickDistance === distance)) {

                core.animationInProgress = false;
                clearInterval(core.animationTimer);

            }

        }, tick);

        return;

    }

    /**
     *
     * @method bindEvents
     * @description binds core event listeners
     * @returns {Object} this
     *
     */
    bindEvents() {

        const { eventCallbacks, props, mode } = this;

        if (mode !== 'MIG') {

            this.clear();

            const { windowResizeCallback, scrollCallback, leftClickCallback, rightClickCallback } = eventCallbacks;
            const { elementList, elementLeft, elementRight } = props;

            window.addEventListener('resize', windowResizeCallback);
            elementList.addEventListener('scroll', scrollCallback);
            elementLeft.addEventListener('click', leftClickCallback);
            elementRight.addEventListener('click', rightClickCallback);

            props.instance = true;

        }

        return this;

    }

    /**
     *
     * @function hideControls
     * @description
     *
     */
    static hideControls(props) {

        const { element } = props;

        element.classList.add('mlb-scores--hide-controls');
        element.classList.remove('mlb-scores--show-controls');

    }

    /**
     *
     * @function hideControls
     * @description
     *
     */
    static showControls(props) {

        const { element } = props;

        element.classList.remove('mlb-scores--hide-controls');
        element.classList.add('mlb-scores--show-controls');

    }

    /**
     *
     * @function clear
     * @description cleans out controls instance
     *
     */
    clear() {

        const { eventCallbacks, props } = this;
        const { windowResizeCallback, scrollCallback, leftClickCallback, rightClickCallback } = eventCallbacks;
        const { elementList, elementLeft, elementRight, mode } = props;

        if (mode !== 'MIG') {

            props.instance = false;

            window.removeEventListener('resize', windowResizeCallback);
            elementList.removeEventListener('scroll', scrollCallback);
            elementLeft.removeEventListener('click', leftClickCallback);
            elementRight.removeEventListener('click', rightClickCallback);

        }

    }

    /**
     *
     * @function bind
     * @description binds core functionality
     * @returns {Object}
     *
     */
    bind() {

        this.bindEvents();

        return this;

    }

    /**
     *
     * @function showHideControls
     * @description show or hide controls
     * @returns {Object}
     *
     */
    showHideControls() {

        const { hideControls, showControls } = scoresControls;
        const { props } = this;

        if (this.shouldShowControls()) {

            showControls(props);

        } else {

            hideControls(props);
        }

        return this;

    }

    /**
     *
     * @method init
     * @description initializes scoresControls instance
     *
     */
    init(selectedGameIdx = 0) {

        const { props, opts } = this;
        const { LazyLoadMIG } = opts;
        const { MIG } = props;

        if (!props.instance) {

            const { controlListSelector, controlLeftSelector, controlRightSelector } = props;
            const { container, mode, Swiper } = opts;

            props.element = container;
            props.elementList = container.querySelector(controlListSelector);
            props.elementLeft = container.querySelector(controlLeftSelector);
            props.elementRight = container.querySelector(controlRightSelector);

            if (mode === 'MIG') {

                LazyLoadMIG.firstTime = true;

                MIG.swiper = new Swiper(container, {
                    // Navigation arrows
                    nextButton: '.mlb-scores__list-button--right',
                    prevButton: '.mlb-scores__list-button--left',
                    wrapperClass: 'mlb-scores__list--games',
                    slideClass: 'mlb-scores__list-item--game',
                    onSlideChangeEnd: () => {
                        LazyLoadMIG.firstTime = false;
                    },
                    onReachBeginning: () => {
                        if (!LazyLoadMIG.firstTime) {
                            this.updatePosition();
                        }
                    },
                    onReachEnd: () => {
                        if (!LazyLoadMIG.firstTime) {
                            this.updatePosition();
                        }
                    },
                    initialSlide: selectedGameIdx,
                    runCallbacksOnInit: false,
                    simulateTouch: false
                });

                this.showHideControls();

            } else {

                this.showHideControls();
                this.updatePosition();

            }

            props.instance = true;

        }

        // idempotent bind. inefficient, but consistent.
        this.bind();

    }

}

export default scoresControls;
